import React, { useMemo } from 'react';
import { Box, Button, Skeleton, Stack, Typography } from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DescriptionIcon from '@mui/icons-material/Description';
import MovieIcon from '@mui/icons-material/Movie';
import ImageIcon from '@mui/icons-material/Image';
import { useNavigate } from 'react-router-dom';

import { useAssets } from '../../queries';
import type { AssetSummary } from '../../domain';

interface Props {
  /** How many tiles to show. Default 8 — one full row at xl, wraps below. */
  limit?: number;
}

/**
 * "Jump back in" — the most recent assets as a thumbnail-forward strip, the
 * Creative Cloud "Recent files" pattern.
 *
 * Every kind is included, not just images: a document or video tile falls back
 * to a typed placeholder so the row reflects everything the user has made
 * rather than silently hiding the Writer's/Animation Lab output.
 */
export const RecentAssetsRow: React.FC<Props> = ({ limit = 8 }) => {
  const navigate = useNavigate();
  const { data, isLoading } = useAssets({ pageSize: limit });

  const items = useMemo<AssetSummary[]>(() => data?.assets?.slice(0, limit) ?? [], [data, limit]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h6" fontWeight={600}>
          Jump back in
        </Typography>
        <Button
          size="small"
          endIcon={<ArrowForwardIcon />}
          onClick={() => navigate('/assets')}
          sx={{ textTransform: 'none' }}
        >
          View all
        </Button>
      </Stack>

      {isLoading ? (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" sx={{ flex: '1 1 160px', minWidth: 150, aspectRatio: '4 / 3' }} />
          ))}
        </Stack>
      ) : items.length === 0 ? (
        <EmptyState onStart={() => navigate('/labs/image-lab')} />
      ) : (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          {items.map(a => (
            <AssetTile key={a.assetId} asset={a} onOpen={() => navigate('/assets')} />
          ))}
        </Stack>
      )}
    </Box>
  );
};

const AssetTile: React.FC<{ asset: AssetSummary; onOpen: () => void }> = ({ asset, onOpen }) => {
  // `signedUrl` is the browser-loadable form — a native <img> can't attach the
  // auth headers the plain `url` needs.
  const src = asset.signedThumbnailUrl ?? asset.signedUrl ?? asset.thumbnailUrl ?? asset.url;
  const isImage = asset.kind === 'image';

  return (
    <Box
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      aria-label={`Open ${asset.kind} asset`}
      sx={{
        flex: '1 1 160px',
        minWidth: 150,
        maxWidth: 260,
        aspectRatio: '4 / 3',
        borderRadius: 2,
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'action.hover',
        transition: 'transform 180ms ease, box-shadow 180ms ease',
        '&:hover, &:focus-visible': {
          transform: 'translateY(-4px)',
          boxShadow: 6,
          outline: 'none',
        },
      }}
    >
      {isImage && src ? (
        <Box
          component="img"
          src={src}
          alt=""
          loading="lazy"
          sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      ) : (
        <Stack alignItems="center" justifyContent="center" sx={{ height: '100%', color: 'text.secondary' }}>
          <KindIcon kind={asset.kind} />
          <Typography variant="caption" sx={{ mt: 0.5 }}>
            {String(asset.kind)}
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

const KindIcon: React.FC<{ kind: string }> = ({ kind }) => {
  if (kind === 'video') return <MovieIcon />;
  if (kind === 'document' || kind === 'text') return <DescriptionIcon />;
  return <ImageIcon />;
};

const EmptyState: React.FC<{ onStart: () => void }> = ({ onStart }) => (
  <Box
    sx={{
      borderRadius: 2,
      border: '1px dashed',
      borderColor: 'divider',
      px: 3,
      py: 5,
      textAlign: 'center',
    }}
  >
    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
      Nothing here yet — your generated images, documents and videos will collect here.
    </Typography>
    <Button variant="contained" onClick={onStart} sx={{ textTransform: 'none', borderRadius: 999 }}>
      Create your first asset
    </Button>
  </Box>
);
